import { projectPathPrefix } from 'helpers/getters';
import REQUEST_CACHE from 'requests/request_cache';
import { preloadImages } from 'requests/helpers';
import Topic from '../../cli/shared/topic';

const requestJson = (topic, options = {}) => {
  if (REQUEST_CACHE[topic.mixedCase]) return REQUEST_CACHE[topic.mixedCase].promise;

  const embeddedTopicScript = document.querySelector(`script[data-topic-json="${topic.jsonFileName}.json"]`);
  const prefix = projectPathPrefix ? `/${projectPathPrefix}` : '';
  const dataPath = `${prefix}/_data/${topic.jsonFileName}.json`;
  const cacheEntry = {
    status: 'pending',
    json: null,
    jsonSizeBytes: null,
    paragraphCount: null,
    promise: null
  };

  const dataPromise = embeddedTopicScript ?
    Promise.resolve(parseJsonText(embeddedTopicScript.textContent, cacheEntry)) : // embedded topic JSON (default topic / single-file build)
    Promise.resolve().then(() => fetch(dataPath, { signal: options.signal })) // wrap to capture sync fetch failures in the promise chain
      .then(res => {
        if (!res.ok) throw new Error(`Missing topic JSON "${topic.jsonFileName}" (status ${res.status})`);
        return res.text();
      })
      .then(text => parseJsonText(text, cacheEntry));

  const requestPromise = dataPromise
    .then(json => {
      preloadImages(json);
      cacheEntry.status = 'fulfilled';
      cacheEntry.json = json;
      return json;
    })
    .catch(() => {
      delete REQUEST_CACHE[topic.mixedCase];
      return null; // ignore aborted fetches or navigation-related rejections
    });

  cacheEntry.promise = requestPromise;

  REQUEST_CACHE[topic.mixedCase] = cacheEntry;
  return requestPromise;
};

function parseJsonText(jsonText, cacheEntry) {
  cacheEntry.jsonSizeBytes = byteLength(jsonText);
  const json = JSON.parse(jsonText);
  cacheEntry.paragraphCount = Object.keys(json?.paragraphsBySubtopic || {}).length;
  return json;
}

function byteLength(text) {
  if (!text) return 0;
  if (typeof TextEncoder === 'function') return new TextEncoder().encode(text).length;
  return text.length;
}

function jsonSizeBytesForTopic(topic) {
  return REQUEST_CACHE[topic.mixedCase]?.jsonSizeBytes;
}

function paragraphCountForTopic(topic) {
  return REQUEST_CACHE[topic.mixedCase]?.paragraphCount;
}

function getCanonicalTopic(topic, subtopic = topic) {
  const matchingEntry = Object.values(REQUEST_CACHE).find(entry =>
    entry.status === 'fulfilled' &&
    entry.json &&
    Topic.for(entry.json.displayTopicName).matches(topic)
  );

  if (!matchingEntry) return subtopic;

  const correctSubtopicKey = Object.keys(matchingEntry.json.paragraphsBySubtopic)
    .find(key => Topic.fromMixedCase(key).matches(subtopic));
  if (!correctSubtopicKey) return subtopic;
  return Topic.fromMixedCase(correctSubtopicKey);
}

export { requestJson, getCanonicalTopic, jsonSizeBytesForTopic, paragraphCountForTopic };
